import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { IndianRupee, ShoppingBag, Receipt, XCircle, Download } from "lucide-react";
import { DatePicker } from "@/components/common/DatePicker";
import { Button } from "@/components/ui/button";
import { api, apiError, money } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common/Primitives";
import { Stat } from "./Overview";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Reports() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 29 * 864e5)));
  const [to, setTo] = useState(iso(new Date()));
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setRep(null);
    setErr("");
    if (from > to) return setErr("The start date must be on or before the end date.");
    if ((new Date(to) - new Date(from)) / 864e5 >= 366) return setErr("Please choose a range of 366 days or fewer.");
    api.get("/restaurant/reports", { params: { from_date: from, to_date: to } })
      .then((r) => setRep(r.data))
      .catch((e) => setErr(apiError(e, "Could not load the report")));
  }, [from, to]);

  const exportCsv = () => {
    const rows = [["Date", "Orders", "Revenue"], ...rep.by_day.map((d) => [d.date, d.orders, d.revenue])];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sales-${from}-to-${to}.csv`;
    a.click();
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Sales, GST and best sellers. Cancelled orders are excluded.">
        <DatePicker value={from} onChange={setFrom} max={to} testId="report-from" />
        <DatePicker value={to} onChange={setTo} min={from} max={iso(new Date())} testId="report-to" />
        <Button variant="outline" onClick={exportCsv} disabled={!rep} data-testid="report-export-btn"><Download className="mr-1 h-4 w-4" />CSV</Button>
      </PageHeader>
      {err ? (
        <p className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="report-error">{err}</p>
      ) : !rep ? <Loader /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={IndianRupee} label="Revenue" value={money(rep.revenue)} testId="report-revenue" />
            <Stat icon={ShoppingBag} label="Orders" value={rep.orders_count} testId="report-orders" />
            <Stat icon={Receipt} label="GST collected" value={money(rep.tax_total)} testId="report-tax" />
            <Stat icon={XCircle} label="Cancelled" value={rep.cancelled} testId="report-cancelled" />
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2">
              <h3 className="mb-4 font-heading font-semibold text-slate-900">Daily revenue</h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={rep.by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Bar dataKey="revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="top-items">
              <h3 className="mb-4 font-heading font-semibold text-slate-900">Top items</h3>
              {rep.top_items.length === 0 ? <p className="text-sm text-slate-400">No sales in this range.</p> : (
                <ol className="space-y-2.5">
                  {rep.top_items.map((t, i) => (
                    <li key={t.name} className="flex items-center justify-between text-sm">
                      <span className="truncate"><span className="mr-2 text-slate-400">{i + 1}.</span>{t.name}</span>
                      <span className="shrink-0 text-slate-500">{t.qty} · {money(t.revenue)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
